import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Calendar, Clock, Award, Mail } from "lucide-react";
import SEO from "../components/SEO";
import "./BlogArticle.css";

export const MakingTaxDigitalArticle = () => {
  return (
    <div className="article-page">
      <SEO
        title="Making Tax Digital Guide UK | MTD for Small Businesses"
        description="Understanding MTD requirements for VAT and income tax, plus how to prepare your business for digital record-keeping. Expert guide from Brightson Accounting."
        canonical="/blog/making-tax-digital-guide"
        keywords="making tax digital, MTD UK, MTD for income tax, digital record keeping, MTD software, Wolverhampton accountant"
      />

      {/* Article Header */}
      <section className="article-header">
        <div className="container">
          <div className="article-back">
            <Link to="/blog" className="back-link">
              <ArrowLeft size={20} />
              Back to Blog
            </Link>
          </div>
          <div className="article-header-content">
            <span className="article-category-badge">Compliance</span>
            <h1 className="article-main-title">Making Tax Digital: What Small Businesses Need to Know</h1>
            <p className="article-subtitle">Understanding MTD requirements for VAT and income tax, plus how to prepare your business for digital record-keeping</p>
            <div className="article-meta-bar">
              <div className="meta-author">
                <Award size={18} />
                <span>Brightson Accounting (FCCA Qualified)</span>
              </div>
              <div className="meta-info">
                <Calendar size={16} />
                <span>28 December 2025</span>
                <Clock size={16} />
                <span>8 min read</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Article Content */}
      <section className="article-content">
        <div className="container">
          <div className="article-body">
            
            <p className="article-lead">
              Making Tax Digital (MTD) is changing how UK businesses report to HMRC. Here's what you need to know.
            </p>

            <p>
              If you're self-employed, a landlord, or run a small business, MTD will likely affect you from April 2026.
            </p>

            <p>
              This guide explains the requirements, deadlines, and what you need to do to stay compliant.
            </p>

            <h2>What is Making Tax Digital?</h2>

            <p>
              Making Tax Digital (MTD) requires businesses to:
            </p>

            <ul>
              <li><strong>Keep digital records</strong> using HMRC-approved software</li>
              <li><strong>Submit quarterly updates</strong> throughout the year</li>
              <li><strong>File a final declaration</strong> after the tax year ends</li>
            </ul>

            <p>
              MTD replaces the traditional Self Assessment process for many taxpayers.
            </p>

            <h2>When Does MTD Apply?</h2>

            <p>
              From <strong>6 April 2026</strong>, MTD for Income Tax applies to:
            </p>

            <ul>
              <li>Self-employed individuals</li>
              <li>Landlords with property income</li>
            </ul>

            <p>
              <strong>MTD applies if your combined gross income (turnover before any expenses) exceeds £50,000, based on your 2024/25 tax return.</strong>
            </p>

            <p>
              If you have multiple income sources (e.g. self-employment + rental income), HMRC combines them.
            </p>

            <p>
              <strong>Example:</strong> £45k trade + £6k rental = £51k → MTD applies.
            </p>

            <p className="note-text">
              The threshold reduces to £30,000 from April 2027.
            </p>

            <h2>Old System vs MTD System</h2>

            <div className="table-responsive">
              <table className="deadline-table">
                <thead>
                  <tr>
                    <th>Old System</th>
                    <th>MTD System</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Once yearly</td>
                    <td>4 updates + final</td>
                  </tr>
                  <tr>
                    <td>Manual records allowed</td>
                    <td>Digital records required</td>
                  </tr>
                  <tr>
                    <td>Filed via HMRC</td>
                    <td>Filed via software</td>
                  </tr>
                  <tr>
                    <td>HMRC sees data annually</td>
                    <td>HMRC sees near real-time</td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Mid-Article CTA */}
            <div className="article-cta-box" style={{ background: '#f8f9fa', border: '2px solid #0066cc', marginTop: '2rem', marginBottom: '2rem' }}>
              <h3>💡 Want to see how much tax you could save?</h3>
              <p>
                Most UK business owners are overpaying without realising it.
              </p>
              <Link to="/corporation-tax-calculator" className="article-cta-button">
                👉 Try the Corporation Tax Calculator
              </Link>
            </div>

            <h2>Quarterly Updates</h2>

            <p>
              Under MTD, you must submit four quarterly updates showing:
            </p>

            <ul>
              <li>Income received</li>
              <li>Expenses paid</li>
              <li>Any changes to your business</li>
            </ul>

            <p>
              <strong>Key Dates:</strong>
            </p>

            <ul>
              <li><strong>7 August 2026</strong> – First MTD submission (first mandatory filing under the new system)</li>
              <li><strong>7 November 2026</strong></li>
              <li><strong>7 February 2027</strong></li>
              <li><strong>7 May 2027</strong></li>
            </ul>

            <p>
              HMRC allows <strong>calendar quarter reporting</strong> (ending 30 June, 30 Sept, 31 Dec, 31 March), which often 
              aligns better with VAT and bank records.
            </p>

            <p>
              We help clients choose the most practical reporting cycle.
            </p>

            <p className="note-text">
              If using calendar quarters, a small adjustment may be required for the April transition — we handle this for our clients.
            </p>

            <h2>Final Declaration</h2>

            <p>
              After submitting four quarterly updates, you must file a <strong>Final Declaration</strong> by:
            </p>

            <p>
              <strong>31 January 2028</strong> (for the 2026/27 tax year)
            </p>

            <p>
              This replaces your Self Assessment tax return.
            </p>

            <h2>Multiple Income Streams</h2>

            <p>
              If you have both business and property income, you must submit <strong>separate MTD updates for each</strong>.
            </p>

            <p>
              Example: A plumber with rental property submits:
            </p>

            <ul>
              <li>Quarterly updates for plumbing business</li>
              <li>Quarterly updates for rental income</li>
              <li>One Final Declaration combining both</li>
            </ul>

            <h2>Software Requirements</h2>

            <p>
              You need <strong>HMRC-approved software</strong> to:
            </p>

            <ul>
              <li>Keep digital records</li>
              <li>Submit quarterly updates</li>
              <li>File your Final Declaration</li>
            </ul>

            <p>
              Popular options include:
            </p>

            <ul>
              <li>Xero</li>
              <li>QuickBooks</li>
              <li>FreeAgent</li>
              <li>Sage</li>
            </ul>

            <p>
              We support businesses across <strong>Wolverhampton, Birmingham and the West Midlands</strong> and are experienced 
              with Xero and QuickBooks setup.
            </p>

            <h2>Penalties and Compliance</h2>

            <p>
              For the first year (2026/27), HMRC has introduced a <strong>"soft landing"</strong> approach:
            </p>

            <ul>
              <li>No penalties for late quarterly updates</li>
              <li>Penalties still apply for late tax payments</li>
            </ul>

            <p className="highlight-note">
              <strong>Important:</strong> While quarterly updates are penalty-free in 2026/27, the Final Declaration 
              (31 January 2028) and tax payments remain fully subject to penalties and interest if missed.
            </p>

            <h2>How to Prepare</h2>

            <p>
              Steps to get MTD-ready:
            </p>

            <ul>
              <li><strong>Check if MTD applies to you</strong> (£50,000+ turnover in 2024/25)</li>
              <li><strong>Choose software</strong> that suits your business</li>
              <li><strong>Digitise your records</strong> (receipts, invoices, bank statements)</li>
              <li><strong>Learn the software</strong> or work with an accountant</li>
              <li><strong>Set reminders</strong> for quarterly deadlines</li>
            </ul>

            <h2>Quick Tips for 2026</h2>

            <ul>
              <li>Check your 2024/25 turnover (not profit)</li>
              <li>Move to digital software early</li>
              <li>Plan for more frequent reporting</li>
              <li>Set aside tax throughout the year</li>
            </ul>

            <h2>How Brightson Accounting Helps</h2>

            <p>
              We support small businesses and landlords with:
            </p>

            <ul>
              <li><strong>MTD software setup</strong> (Xero, QuickBooks)</li>
              <li><strong>Quarterly submission management</strong> (we file on your behalf)</li>
              <li><strong>Final Declaration preparation</strong></li>
              <li><strong>Tax planning</strong> (spreading liabilities across the year)</li>
              <li><strong>Training</strong> (helping you use software confidently)</li>
            </ul>

            <p>
              All with clear communication and fixed pricing.
            </p>

            <div className="article-cta-box">
              <h2>Need Help with MTD?</h2>
              <p>
                We'll set up your software, handle your quarterly submissions, and ensure you never miss a deadline.
              </p>
              <Link to="/contact" className="article-cta-button">
              <Mail size={20} />
                Book a Free MTD Consultation
              </Link>
            </div>

            <div className="disclaimer">
              <h3>Disclaimer</h3>
              <p>
                This guide is for general information only and reflects HMRC rules as of December 2025. Tax legislation may change. 
                For personalised advice, contact Brightson Accounting.
              </p>
            </div>

          </div>

          {/* Sidebar */}
          <aside className="article-sidebar">
            <div className="sidebar-card">
              <h3 className="sidebar-heading">MTD Support</h3>
              <p>Get expert help setting up software and managing quarterly submissions.</p>
              <Link to="/contact" className="sidebar-button">
                <Mail size={18} />
                Contact Us
              </Link>
            </div>

            <div className="sidebar-card">
              <h3 className="sidebar-heading">Related Articles</h3>
              <div className="related-links">
                <Link to="/blog/tax-deadlines-small-businesses-2026-27" className="related-link">
                  Tax Deadlines 2026/27
                </Link>
                <Link to="/blog/self-assessment-guide-first-time" className="related-link">
                  Self Assessment Guide
                </Link>
                <Link to="/blog" className="related-link">All Blog Articles</Link>
              </div>
            </div>

            <div className="sidebar-card">
              <h3 className="sidebar-heading">Our Services</h3>
              <div className="related-links">
                <Link to="/services" className="related-link">MTD Setup & Support</Link>
                <Link to="/services" className="related-link">Tax Planning</Link>
                <Link to="/pricing" className="related-link">Pricing</Link>
                <Link to="/contact" className="related-link">Contact</Link>
              </div>
            </div>
          </aside>
        </div>
      </section>
    </div>
  );
};

export default MakingTaxDigitalArticle;
